// Quick Count - Step 2: Count Items
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  ArrowRight,
  ClipboardList,
  Search,
  X,
  CheckCircle2,
  AlertCircle,
  Package,
  Filter,
  Download,
  Save,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { mockWarehouses } from "@/data/mockWarehousesLocations";
import { format } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import {
  saveStockTake,
  generateStockTakeId,
  generateStockTakeReference,
  SavedStockTake,
} from "@/lib/stock-take-storage";

// Import mock stock items from stock items page
import { generateMockStockItems } from "@/data/mockStockItems";

interface CountItem {
  code: string;
  description: string;
  category: string;
  location: string;
  systemQty: number;
  unit: string;
  countedQty: string;
  countedBy: string;
  notes: string;
  isCounted: boolean;
}

interface QuickCountCountPageProps {
  formData?: {
    date: string;
    warehouse: string;
    assignedTo: string;
    description: string;
    notes: string;
  };
}

export function StockTakesQuickCountCountPage({ formData }: QuickCountCountPageProps) {
  const { navigateTo } = useNavigation();
  
  // Initialize count items based on warehouse filter
  const initialItems = useMemo(() => {
    const allStockItems = generateMockStockItems();
    let items = allStockItems;
    
    // Filter by warehouse if selected
    if (formData?.warehouse && formData.warehouse !== "no-warehouse" && formData.warehouse !== "all") {
      items = items.filter(item => item.warehouseCode === formData.warehouse);
    }
    
    return items.map(item => ({
      code: item.code,
      description: item.description,
      category: item.category,
      location: `${item.warehouseCode} - ${item.locationCode}`,
      systemQty: item.physicalQuantity,
      unit: item.unitOfMeasure,
      countedQty: "",
      countedBy: formData?.assignedTo || "", // Auto-fill from assigned user
      notes: "",
      isCounted: false,
    }));
  }, [formData?.warehouse, formData?.assignedTo]);

  const [countItems, setCountItems] = useState<CountItem[]>(initialItems);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showOnlyVariances, setShowOnlyVariances] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Get unique categories
  const categories = useMemo(() => {
    return Array.from(new Set(countItems.map(item => item.category)));
  }, [countItems]);

  // Filter items
  const filteredItems = useMemo(() => {
    let filtered = countItems;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        item =>
          item.code.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }

    // Status filter
    if (statusFilter === "counted") {
      filtered = filtered.filter(item => item.isCounted);
    } else if (statusFilter === "uncounted") {
      filtered = filtered.filter(item => !item.isCounted);
    }

    // Variance filter
    if (showOnlyVariances) {
      filtered = filtered.filter(item => {
        if (!item.isCounted || !item.countedQty) return false;
        const counted = parseFloat(item.countedQty);
        return !isNaN(counted) && counted !== item.systemQty;
      });
    }

    return filtered;
  }, [countItems, searchQuery, categoryFilter, statusFilter, showOnlyVariances]);

  // Paginate filtered items
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentPage, itemsPerPage]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startItem = filteredItems.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, filteredItems.length);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter, statusFilter, showOnlyVariances]);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) {
        pages.push("...");
      }
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) {
        pages.push("...");
      }
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    return pages;
  };

  // Statistics
  const stats = useMemo(() => {
    const total = countItems.length;
    const counted = countItems.filter(item => item.isCounted).length;
    const uncounted = total - counted;
    const variances = countItems.filter(item => {
      if (!item.isCounted || !item.countedQty) return false;
      const countedQty = parseFloat(item.countedQty);
      return !isNaN(countedQty) && countedQty !== item.systemQty;
    }).length;

    return { total, counted, uncounted, variances };
  }, [countItems]);

  const updateCountItem = (code: string, field: keyof CountItem, value: string | boolean) => {
    setCountItems(prev =>
      prev.map(item => {
        if (item.code === code) {
          const updated = { ...item, [field]: value };
          
          // Auto-mark as counted when quantity is entered
          if (field === "countedQty" && value !== "") {
            updated.isCounted = true;
          }
          
          return updated;
        }
        return item;
      })
    );
  };

  const getVariance = (item: CountItem) => {
    if (!item.countedQty) return null;
    const counted = parseFloat(item.countedQty);
    if (isNaN(counted)) return null;
    return counted - item.systemQty;
  };

  const getVarianceBadge = (variance: number | null) => {
    if (variance === null) return null;
    if (variance === 0) {
      return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Match</Badge>;
    } else if (variance > 0) {
      return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">+{variance}</Badge>;
    } else {
      return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{variance}</Badge>;
    }
  };

  const handleSaveDraft = () => {
    try {
      const stockTakeId = generateStockTakeId();
      const reference = generateStockTakeReference();
      
      const savedStockTake: SavedStockTake = {
        id: stockTakeId,
        reference,
        description: formData?.description || `Quick Count - ${format(new Date(formData?.date || new Date()), "MMM dd, yyyy")}`,
        type: 'Quick Count',
        status: 'In Progress',
        currentStep: 2,
        createdAt: new Date(),
        lastModified: new Date(),
        createdBy: 'Current User',
        setupData: {
          countDate: formData?.date || new Date().toISOString().split('T')[0],
          warehouse: formData?.warehouse || '',
          assignedTo: formData?.assignedTo || '',
        },
        countData: {
          countItems: countItems.map(item => ({
            code: item.code,
            description: item.description,
            category: item.category,
            systemQty: item.systemQty,
            countedQty: item.countedQty,
            variance: item.countedQty ? (parseInt(item.countedQty) || 0) - item.systemQty : 0,
            location: item.location,
            unit: item.unit,
            countedBy: item.countedBy,
            isCounted: item.isCounted,
          })),
        },
      };
      
      saveStockTake(savedStockTake);
      toast.success("Stock take saved successfully! You can resume it later from the Stock Takes page.");
      navigateTo("stock-takes");
    } catch (error) {
      toast.error("Failed to save stock take. Please try again.");
    }
  };

  const handleNext = () => {
    // Check if all items are counted
    const uncountedItems = countItems.filter(item => !item.isCounted);
    
    if (uncountedItems.length > 0) {
      toast.error(`${uncountedItems.length} item(s) still need to be counted`);
      return;
    }

    // Navigate to review step with proper data structure
    const countedItems = countItems.map(item => ({
      id: item.code,
      itemCode: item.code,
      description: item.description,
      category: item.category,
      systemQty: item.systemQty,
      countedQty: parseInt(item.countedQty) || 0,
      variance: (parseInt(item.countedQty) || 0) - item.systemQty,
      countedBy: item.countedBy,
      warehouse: item.location,
      unit: item.unit,
    }));
    
    navigateTo("stock-takes/quick-count/review", { 
      setupData: {
        countDate: formData?.date || new Date().toISOString().split('T')[0],
        warehouse: formData?.warehouse || '',
        assignedTo: formData?.assignedTo || '',
      },
      countedItems 
    });
  };

  const handleBack = () => {
    navigateTo("stock-takes/quick-count/setup");
  };

  const warehouseName = formData?.warehouse && formData.warehouse !== "no-warehouse"
    ? mockWarehouses.find(w => w.code === formData.warehouse)?.name
    : "All Warehouses";

  return (
    <div className="flex flex-col h-full max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <ClipboardList className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h1 className="text-3xl font-bold tracking-tight">Quick Count</h1>
              <Badge variant="default">Step 2 of 3</Badge>
            </div>
            <p className="text-muted-foreground mt-1">
              Count physical stock quantities
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleSaveDraft}>
            <Save className="mr-2 h-4 w-4" />
            Save Draft
          </Button>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white text-sm font-medium">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <span className="text-sm font-medium text-green-600">Setup</span>
          </div>
          <div className="flex-1 h-px bg-blue-600" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-medium">
              2
            </div>
            <span className="text-sm font-medium">Count</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 text-sm font-medium">
              3
            </div>
            <span className="text-sm text-muted-foreground">Review</span>
          </div>
        </div>
      </div>

      {/* Count Info Card */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Count Date</Label>
              <p className="text-sm font-medium">{formData?.date ? format(new Date(formData.date), "PPP") : "N/A"}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Warehouse</Label>
              <p className="text-sm font-medium">{warehouseName}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Assigned To</Label>
              <p className="text-sm font-medium">{formData?.assignedTo || "Unassigned"}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <p className="text-sm font-medium">{formData?.description || "N/A"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Items</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Package className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Counted</p>
                <p className="text-2xl font-bold text-green-600">{stats.counted}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Remaining</p>
                <p className="text-2xl font-bold text-orange-600">{stats.uncounted}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Variances</p>
                <p className="text-2xl font-bold text-red-600">{stats.variances}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by code or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7"
                    onClick={() => setSearchQuery("")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="counted">Counted</SelectItem>
                <SelectItem value="uncounted">Uncounted</SelectItem>
              </SelectContent>
            </Select>

            {/* Variance Toggle */}
            <div className="flex items-center space-x-2 border rounded-md px-3">
              <Checkbox
                id="variances"
                checked={showOnlyVariances}
                onCheckedChange={(checked) => setShowOnlyVariances(checked as boolean)}
              />
              <Label htmlFor="variances" className="text-sm cursor-pointer">
                Show only variances
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Count Table */}
      <Card className="flex-1 flex flex-col">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Stock Items</CardTitle>
              <CardDescription>
                Enter physical count for each item
              </CardDescription>
            </div>
            <Badge variant="outline">
              {filteredItems.length} of {countItems.length} items
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Code</TableHead>
                <TableHead className="min-w-[250px]">Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">System Qty</TableHead>
                <TableHead className="text-right w-[120px]">Counted Qty *</TableHead>
                <TableHead className="text-center">Variance</TableHead>
                <TableHead className="w-[250px]">Notes</TableHead>
                <TableHead className="text-center w-[80px]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No items found matching your filters
                  </TableCell>
                </TableRow>
              ) : (
                paginatedItems.map((item) => {
                  const variance = getVariance(item);
                  return (
                    <TableRow key={item.code} className={item.isCounted ? "bg-green-50/50 dark:bg-green-900/10" : ""}>
                      <TableCell className="font-medium">{item.code}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.category}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.location}</TableCell>
                      <TableCell className="text-right font-medium">
                        {item.systemQty} {item.unit}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          placeholder="0"
                          value={item.countedQty}
                          onChange={(e) => updateCountItem(item.code, "countedQty", e.target.value)}
                          className="text-right"
                          min="0"
                          step="1"
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        {getVarianceBadge(variance)}
                      </TableCell>
                      <TableCell>
                        <Textarea
                          placeholder="Optional notes"
                          value={item.notes}
                          onChange={(e) => updateCountItem(item.code, "notes", e.target.value)}
                          className="text-sm min-h-[60px] resize-none"
                          rows={2}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        {item.isCounted ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-orange-400 mx-auto" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <CardContent className="border-t pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground">
                  Showing {startItem} to {endItem} of {filteredItems.length} items
                </p>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[100px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 / page</SelectItem>
                    <SelectItem value="25">25 / page</SelectItem>
                    <SelectItem value="50">50 / page</SelectItem>
                    <SelectItem value="100">100 / page</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                {getPageNumbers().map((page, index) => (
                  <Button
                    key={index}
                    variant={currentPage === page ? "default" : "outline"}
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => typeof page === "number" && setCurrentPage(page)}
                    disabled={typeof page !== "number"}
                  >
                    {page}
                  </Button>
                ))}

                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t">
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Setup
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleSaveDraft}>
            <Save className="mr-2 h-4 w-4" />
            Save Draft
          </Button>
          <Button onClick={handleNext}>
            Review & Submit
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
